import React, { useState } from "react";
import Layout from "../../Layout";
import { useNavigate } from "react-router-dom";

const BundleService = () => {
  const [bundleName, setBundleName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [scheduleType, setScheduleType] = useState("Booked in sequence");
  const [priceType, setPriceType] = useState("Service pricing");
  const [retailPrice, setRetailPrice] = useState("");
  const [onlineBooking, setOnlineBooking] = useState(true);
  const [availableFor, setAvailableFor] = useState("All genders");
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto bg-dry text-text min-h-screen p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">New bundle</h1>
          <div className="flex gap-3">
            <button
              className="px-4 py-2 rounded-md border border-border mr-2 hover:bg-redlight hover:text-white"
              onClick={() => navigate(-1)}
            >
              Close
            </button>
            <button className="px-4 py-2 rounded-md bg-redlight text-white hover:bg-reddark hover:text-white">
              Save
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-4">Basic info</h2>

          {/* Bundle name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-textGray mb-1">
              Bundle name
            </label>
            <input
              type="text"
              value={bundleName}
              onChange={(e) => setBundleName(e.target.value)}
              placeholder="Add a bundle name, e.g. Cut and blow-dry"
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-dry placeholder:text-textGray"
            />
          </div>

          {/* Category */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-textGray mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-dry text-textGray 
             focus:outline-none focus:ring-2 focus:ring-redlight focus:border-redlight"
            >
              <option value="">Select a category</option>
              <option value="hair">Hair</option>
              <option value="nails">Nails</option>
              <option value="spa">Spa</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              The category displayed to you, and to clients online
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-textGray mb-1">
              Description (Optional)
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description about this bundle"
              className="w-full border border-border placehover:text-textGray bg-dry rounded-md px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-400 text-right">
              {description.length}/1000
            </p>
          </div>
        </section>

        {/* Services */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-4">Services</h2>
          <p className="text-sm text-gray-500 mb-4">
            Select which services to include in this bundle and how they should
            be sequenced when booked.
          </p>
          <button className="mb-6 px-4 py-2 border border-border rounded-md text-sm hover:text-redlight">
            + Add service
          </button>

          <div className="">
            <label className="block text-sm font-medium text-textGray mb-1">
              Schedule type
            </label>
            <select
              value={scheduleType}
              onChange={(e) => setScheduleType(e.target.value)}
              className="w-full border border-border bg-dry placeholder:text-textGray rounded-md px-3 py-2 text-sm"
            >
              <option>Booked in sequence</option>
              <option>Booked in parallel</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Select whether the above services will be delivered one after
              another in sequence, or at the same time in parallel.
            </p>
          </div>
        </section>

        {/* Pricing */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-4">Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-textGray mb-1">
                Price type
              </label>
              <select
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
                className="w-full border border-border bg-dry placeholder:text-textGray  rounded-md px-3 py-2 text-sm"
              >
                <option>Service pricing</option>
                <option>Fixed price</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textGray mb-1">
                Retail price
              </label>
              <input
                type="number"
                value={retailPrice}
                onChange={(e) => setRetailPrice(e.target.value)}
                placeholder="NZ$ 0"
                className="w-full border border-border bg-dry placeholder:text-textGray rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">No discount applied</p>
            </div>
          </div>
        </section>

        {/* Online booking */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Online booking</h2>
            <button
              onClick={() => setOnlineBooking(!onlineBooking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                onlineBooking ? "bg-redlight" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  onlineBooking ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Allow clients to book this bundle online via your marketplace,
            socials and booking links
          </p>

          <div className="max-w-xs">
            <label className="block text-sm font-medium text-textGray mb-1">
              Available for
            </label>
            <select
              value={availableFor}
              onChange={(e) => setAvailableFor(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm border-border bg-dry placeholder:text-textGray mb-60 "
            >
              <option>All genders</option>
              <option>Female only</option>
              <option>Male only</option>
            </select>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default BundleService;
